import React, { useEffect, useState } from "react";
import { IAddTestSeries, ITestSeriesId } from "../../TestSeries.props";
import { useForm } from "react-hook-form";
import Loader from "../../../../components/Loader";
import useEditTestSeries from "./useEditTestSeries";
import { CancelButton, FormButton } from "../../../../components/Botton";
import { Switch, FormControlLabel } from "@mui/material";

const EditTestSeries: React.FC<ITestSeriesId> = ({ testSeriesId }) => {
  const {
    formData,
    loading,
    languages,
    courses,
    standard,
    testSeries,
    handleChange,
    handleSubmit,
    handleCancel,
  } = useEditTestSeries(testSeriesId);

  const {
    register,
    handleSubmit: useFormSubmit,
    setValue,
    formState: { errors },
  } = useForm<IAddTestSeries>();

  // Add state for filtering
  const [selectedStandard, setSelectedStandard] = useState<string | undefined>(undefined);
  const [selectedCourse, setSelectedCourse] = useState<string | undefined>(undefined);
  const [filteredCourses, setFilteredCourses] = useState(courses || []);
  console.log(formData)

  // Effect to set initial values
  useEffect(() => {
    if (formData) {
      Object.keys(formData).forEach((key) => {
        setValue(key as keyof IAddTestSeries, formData[key as keyof IAddTestSeries]);
      });
      // Set initial filter states based on formData
      setSelectedStandard(formData.standard);
      setSelectedCourse(formData.courseName);
    }
  }, [formData, setValue]);

  // Effect for filtering courses based on standard
  useEffect(() => {
    if (selectedStandard) {
      const filtered = courses?.filter((course) => course.standard === selectedStandard);
      setFilteredCourses(filtered || []);
    } else {
      setFilteredCourses([]);
    }
  }, [selectedStandard, courses]);

  const fields = [
    {
      label: "Language",
      name: "languageName",
      type: "select",
      options: languages,
    },
    {
      label: "Education",
      name: "standard",
      type: "select",
      options: standard,
    },
    {
      label: "Stream",
      name: "courseName",
      type: "select",
      options: filteredCourses,
    },
    {
      label: "TestSeries Name",
      name: "testSeriesName",
      type: "text",
    },
    {
      label: "Duration of Test (Minutes)",
      name: "duration",
      type: "number",
    },
    {
      label: "Amount",
      name: "amount",
      type: "number",
    },
  ] as const;

  // Calculate subject-level question counts
  const getSubjectLevelCounts = () => {
    if (!testSeries?.questions || !Array.isArray(testSeries.questions)) {
      return [];
    }

    const subjectMap = new Map<string, Map<string, number>>();

    testSeries.questions.forEach((question: any) => {
      const subject = question.subject;
      const level = question.level;

      if (!subjectMap.has(subject)) {
        subjectMap.set(subject, new Map<string, number>());
      }

      const levelMap = subjectMap.get(subject)!;
      levelMap.set(level, (levelMap.get(level) || 0) + 1);
    });

    const result: Array<{ subject: string; level: string; count: number }> = [];

    subjectMap.forEach((levelMap, subject) => {
      levelMap.forEach((count, level) => {
        result.push({ subject, level, count });
      });
    });

    return result;
  };

  const subjectLevelData = getSubjectLevelCounts();

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full ">
        <div className="border-b-2 w-[100%]">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Edit Test Series</h1>
        </div>


        {loading && <Loader />}
        <form onSubmit={useFormSubmit(handleSubmit)} className="space-y-6">
          <fieldset className="m-2 rounded border-opacity-60 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 m-4">
              {fields.map((field) => (
                <div key={field.name}>
                  <label
                    className="block text-gray-700 text-base font-semibold mb-2"
                    htmlFor={field.name}
                  >
                    {field.label}:
                  </label>
                  {field.type === "select" ? (
                    <select
                      id={field.name}
                      {...register(field.name, { required: true })}
                      onChange={(e) => {
                        handleChange(e);
                        if (field.name === "standard") {
                          setSelectedStandard(e.target.value);
                          setSelectedCourse(undefined); // Reset course when standard changes
                        } else if (field.name === "courseName") {
                          setSelectedCourse(e.target.value);
                        }
                      }}
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      disabled={field.name === "courseName" && !selectedStandard}
                      value={formData[field.name]}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options.map((option) => (
                        <option key={option._id} value={option[field.name]}>
                          {option[field.name]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline"
                      type={field.type}
                      id={field.name}
                      {...register(field.name, { required: true })}
                      onChange={handleChange}
                    />
                  )}
                  {errors[field.name] && (
                    <span className="text-red-500 text-xs italic">
                      {field.label} field is required
                    </span>
                  )}
                </div>
              ))}

              {/* Premium/Free Toggle Switch */}
              <div className="col-span-2">
                <label className="block text-gray-700 text-base font-semibold mb-3">
                  Test Series Type:
                </label>
                <div className="flex items-center gap-4">
                  <span className="inline-flex items-center px-4 py-1.5 rounded-md text-white font-bold text-sm"
                    style={{
                      background: formData.isPaid
                        ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        : "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                      boxShadow: formData.isPaid
                        ? "0 2px 8px rgba(102, 126, 234, 0.3)"
                        : "0 2px 8px rgba(17, 153, 142, 0.3)",
                      minWidth: "95px",
                      textAlign: "center",
                    }}
                  >
                    {formData.isPaid ? "⭐ Premium" : "Free"}
                  </span>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isPaid}
                        onChange={(e) => handleChange({ target: { id: 'isPaid', value: e.target.checked } } as any)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: '#667eea',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: '#667eea',
                          },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm text-gray-600 ml-1">
                        {formData.isPaid ? 'Switch to Free' : 'Switch to Premium'}
                      </span>
                    }
                  />
                </div>
              </div>
            </div>
          </fieldset>
          <div className="flex justify-start mt-6 ml-10">
            <FormButton
              className="py-2 px-4 rounded mr-4"
              type="submit"
              children="Update"
            />
            <CancelButton
              className="py-2 px-4 rounded"
              onClick={handleCancel}
              children="Cancel"
              type="button"
            />
          </div>
        </form>

        {/* Subject Level Question Count Table */}
        {subjectLevelData.length > 0 && (
          <div className="m-5 mt-8">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              Question Distribution
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 rounded-lg shadow-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      Subject Name
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      Level
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 border-b">
                      Total Questions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {subjectLevelData.map((item, index) => (
                    <tr
                      key={`${item.subject}-${item.level}-${index}`}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 text-sm text-gray-800 border-b">
                        {item.subject}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800 border-b">
                        <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          {item.level}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-semibold text-gray-800 border-b">
                        {item.count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditTestSeries;